<template>
    <section class="calc-run-form">
        <div class="form-body">
            <b-alert v-if="submitted && validationMessage" show variant="warning" class="mb-3 compact-alert">
                {{ validationMessage }}
            </b-alert>

            <div class="form-section">
                <div class="section-header">
                    <h5>기간 설정</h5>
                    <span>계산 기준 월을 선택해주세요.</span>
                </div>
                <div class="period-flow">
                    <div class="period-step">
                        <div class="step-indicator">
                            <div class="step-number">1</div>
                        </div>
                        <div class="step-content">
                            <label class="step-label">기준 월</label>
                            <DatePicker
                                v-model="local.period"
                                type="month"
                                format="YYYY-MM"
                                value-type="format"
                                :disabled="loading"
                                placeholder="YYYY-MM"
                                class="period-picker"
                                :clearable="false"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-section">
                <div class="section-header">
                    <h5>경계 설정</h5>
                    <span>조직과 경계를 선택해 대상 범위를 지정하세요.</span>
                </div>

                <!-- Drill down flow with selectable cards -->
                <div class="drill-down-flow">
                    <!-- Step 2: 조직 -->
                    <div class="drill-step" :class="{ active: !local.orgCode, completed: local.orgCode }">
                        <div class="step-indicator">
                            <div class="step-number">2</div>
                        </div>
                        <div class="step-content">
                            <label class="step-label">조직</label>
                            <transition name="fade" mode="out-in">
                                <div v-if="!local.orgCode" key="cards" class="step-cards">
                                    <div
                                        v-for="org in organizationOptions"
                                        :key="org.value"
                                        class="selectable-card"
                                        :class="{ disabled: loading }"
                                        @click="!loading && selectOrganization(org.value)"
                                    >
                                        <div class="calc-card-title-row">
                                            <div class="calc-card-title">{{ org.label }}</div>
                                            <div v-if="org.status" class="card-badges">
                                                <span class="card-badge" :class="`status-${org.status}`">
                                                    {{ formatStatus(org.status) }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="card-meta-group">
                                            <div v-if="org.industry" class="card-meta">
                                                <i class="fas fa-industry"></i>
                                                {{ org.industry }}
                                            </div>
                                            <div v-if="org.method" class="card-meta">
                                                <i class="fas fa-balance-scale"></i>
                                                {{ org.method }}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="organizationOptions.length === 0" class="empty-message">
                                        조회 가능한 조직이 없습니다
                                    </div>
                                </div>
                                <div v-else key="selected" class="step-selected">
                                    <span class="selected-value">{{ selectedOrgName }}</span>
                                    <button
                                        type="button"
                                        class="step-change-btn"
                                        @click="clearSelection('orgCode')"
                                        :disabled="loading"
                                    >
                                        변경
                                    </button>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- Step 3: 조직 경계 -->
                    <div
                        v-if="local.orgCode"
                        class="drill-step"
                        :class="{
                            active: local.orgCode && !local.orgBoundaryIdx,
                            completed: local.orgBoundaryIdx,
                        }"
                    >
                        <div class="step-indicator">
                            <div class="step-number">3</div>
                        </div>
                        <div class="step-content">
                            <label class="step-label">조직 경계</label>
                            <transition name="fade" mode="out-in">
                                <div v-if="!local.orgBoundaryIdx" key="cards" class="step-cards">
                                    <div
                                        v-for="boundary in orgBoundaryOptions"
                                        :key="boundary.value"
                                        class="selectable-card"
                                        :class="{ disabled: loading }"
                                        @click="!loading && selectOrgBoundary(boundary.value)"
                                    >
                                        <div class="calc-card-title-row">
                                            <div class="calc-card-title">{{ boundary.label }}</div>
                                            <div v-if="boundary.draftStat" class="card-badges">
                                                <span class="card-badge" :class="`status-${boundary.draftStat}`">
                                                    {{ formatDraftStat(boundary.draftStat) }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="card-meta-group">
                                            <div v-if="boundary.accountingMethod" class="card-meta">
                                                <i class="fas fa-balance-scale"></i>
                                                {{ boundary.accountingMethod }}
                                            </div>
                                            <div v-if="boundary.period" class="card-meta">
                                                <i class="fas fa-calendar-alt"></i>
                                                {{ boundary.period }}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="orgBoundaryOptions.length === 0" class="empty-message">
                                        선택한 조직에 대한 조직 경계가 없습니다
                                    </div>
                                </div>
                                <div v-else key="selected" class="step-selected">
                                    <span class="selected-value">{{ selectedOrgBoundaryName }}</span>
                                    <button
                                        type="button"
                                        class="step-change-btn"
                                        @click="clearSelection('orgBoundaryIdx')"
                                        :disabled="loading"
                                    >
                                        변경
                                    </button>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- Step 4: 운영 경계 -->
                    <div
                        v-if="local.orgCode && local.orgBoundaryIdx"
                        class="drill-step"
                        :class="{
                            active: local.orgCode && local.orgBoundaryIdx && !local.operBoundaryIdx,
                            completed: local.operBoundaryIdx,
                        }"
                    >
                        <div class="step-indicator">
                            <div class="step-number">4</div>
                        </div>
                        <div class="step-content">
                            <label class="step-label">운영 경계</label>
                            <transition name="fade" mode="out-in">
                                <div v-if="!local.operBoundaryIdx" key="cards" class="step-cards">
                                    <div
                                        v-for="boundary in operBoundaryOptions"
                                        :key="boundary.value"
                                        class="selectable-card"
                                        :class="{ disabled: loading }"
                                        @click="!loading && selectOperBoundary(boundary.value)"
                                    >
                                        <div class="calc-card-title-row">
                                            <div class="calc-card-title">{{ boundary.label }}</div>
                                            <div v-if="boundary.draftStat" class="card-badges">
                                                <span class="card-badge" :class="`status-${boundary.draftStat}`">
                                                    {{ formatDraftStat(boundary.draftStat) }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="card-meta-group">
                                            <div v-if="boundary.period" class="card-meta">
                                                <i class="fas fa-calendar-alt"></i>
                                                {{ boundary.period }}
                                            </div>
                                            <div
                                                v-if="boundary.scope && boundary.scope !== '-'"
                                                class="card-meta card-scope"
                                            >
                                                <i class="fas fa-info-circle"></i>
                                                {{
                                                    boundary.scope.length > 30
                                                        ? boundary.scope.substring(0, 30) + "..."
                                                        : boundary.scope
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="operBoundaryOptions.length === 0" class="empty-message">
                                        선택한 조직에 대한 운영 경계가 없습니다
                                    </div>
                                </div>
                                <div v-else key="selected" class="step-selected">
                                    <span class="selected-value">{{ selectedOperBoundaryName }}</span>
                                    <button
                                        type="button"
                                        class="step-change-btn"
                                        @click="clearSelection('operBoundaryIdx')"
                                        :disabled="loading"
                                    >
                                        변경
                                    </button>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-section">
                <div class="section-header">
                    <h5>배출계수 버전</h5>
                    <span>적용할 배출계수 버전을 선택합니다.</span>
                </div>

                <div class="drill-step" :class="{ active: !local.versionIdx, completed: local.versionIdx }">
                    <div class="step-indicator">
                        <div class="step-number">5</div>
                    </div>
                    <div class="step-content">
                        <label class="step-label">버전</label>
                        <transition name="fade" mode="out-in">
                            <div v-if="!local.versionIdx" key="cards" class="step-cards">
                                <div
                                    v-for="version in versionCards"
                                    :key="version.value"
                                    class="selectable-card"
                                    :class="{ disabled: loading, 'card-active': version.isActive }"
                                    @click="!loading && selectVersion(version.value)"
                                >
                                    <div class="calc-card-title-row">
                                        <div class="calc-card-title">{{ version.label }}</div>
                                        <div class="card-badges">
                                            <span v-if="version.isActive" class="card-badge badge-active">활성</span>
                                            <span
                                                v-if="version.draftStat"
                                                class="card-badge"
                                                :class="`status-${version.draftStat}`"
                                            >
                                                {{ formatDraftStat(version.draftStat) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="card-meta-group">
                                        <div v-if="version.code" class="card-meta">
                                            <i class="fas fa-code"></i>
                                            {{ version.code }}
                                        </div>
                                        <div v-if="version.gwpVersion" class="card-meta">
                                            <i class="fas fa-globe"></i>
                                            GWP: {{ version.gwpVersion }}
                                        </div>
                                        <div v-if="version.period" class="card-meta">
                                            <i class="fas fa-calendar-alt"></i>
                                            {{ version.period }}
                                        </div>
                                    </div>
                                </div>
                                <div v-if="versionCards.length === 0" class="empty-message">
                                    사용 가능한 배출계수 버전이 없습니다
                                </div>
                            </div>
                            <div v-else key="selected" class="step-selected">
                                <span class="selected-value">
                                    {{ selectedVersion.versionName }} ({{ selectedVersion.versionCode }})
                                </span>
                                <button
                                    type="button"
                                    class="step-change-btn"
                                    @click="clearSelection('versionIdx')"
                                    :disabled="loading"
                                >
                                    변경
                                </button>
                            </div>
                        </transition>
                        <div
                            v-if="selectedVersion && (selectedVersion.validFrom || selectedVersion.validTo)"
                            class="step-meta"
                        >
                            <i class="fas fa-calendar-alt"></i>
                            <span
                                >{{ formatDate(selectedVersion.validFrom) }} ~
                                {{ formatDate(selectedVersion.validTo) }}</span
                            >
                        </div>
                    </div>
                </div>
            </div>

            <div class="action-row">
                <b-button
                    variant="success"
                    size="lg"
                    class="action-btn"
                    :disabled="!isValid || loading"
                    @click="handleSubmit"
                >
                    <span class="ml-2">{{ loading ? "실행 중..." : "계산 & 인벤토리 생성" }}</span>
                </b-button>
            </div>
        </div>
    </section>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonCodes from "../../../codes";

export default {
    name: "CalcRunForm",
    components: {
        DatePicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        organizations: {
            type: Array,
            default: () => [],
        },
        selectedOrgCode: {
            type: String,
            default: null,
        },
        orgBoundaries: {
            type: Array,
            default: () => [],
        },
        operBoundaries: {
            type: Array,
            default: () => [],
        },
        versions: {
            type: Array,
            default: () => [],
        },

        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            local: {
                orgCode: this.value.orgCode ?? this.selectedOrgCode ?? null,
                period: this.value.period,
                orgBoundaryIdx: this.value.orgBoundaryIdx || null,
                operBoundaryIdx: this.value.operBoundaryIdx || null,
                versionIdx: this.value.versionIdx || null,
            },
            submitted: false,
        };
    },
    computed: {
        organizationOptions() {
            return (this.organizations || []).map((org) => ({
                value: org.orgCode || org.orgIdx,
                label: org.orgName || org.orgCode || "조직",
                industry: org.industry || "",
                method: this.formatAccountingMethod(org.accountingMethod),
                status: org.status || "",
            }));
        },
        orgBoundaryOptions() {
            if (!this.orgBoundaries || this.orgBoundaries.length === 0) {
                return [];
            }

            return this.orgBoundaries
                .map((boundary) => {
                    // boundaryService.js에서 이미 boundaryIdx와 boundaryName으로 통일되어 있음
                    // 하지만 원본 필드명도 fallback으로 지원 (안전성)
                    const boundaryIdx = boundary.boundaryIdx ?? boundary.orgBoundaryIdx ?? null;
                    const boundaryName =
                        boundary.boundaryName ?? boundary.orgBoundaryName ?? boundary.name ?? "조직 경계";

                    if (!boundaryIdx) {
                        console.warn("[CalcRunForm] 조직 경계에 boundaryIdx가 없습니다:", boundary);
                    }

                    return {
                        value: boundaryIdx,
                        label: boundaryName,
                        accountingMethod: this.formatAccountingMethod(boundary.accountingMethod),
                        period: this.formatPeriod(boundary.startDate, boundary.endDate),
                        remarks: boundary.remarks || "",
                        draftStat: boundary.draftStat || "",
                    };
                })
                .filter((option) => option.value != null); // 유효한 값만 필터링
        },
        operBoundaryOptions() {
            return (this.operBoundaries || []).map((boundary) => ({
                value: boundary.operBoundaryIdx,
                label: boundary.operBoundaryName || boundary.boundaryName || boundary.name || "운영 경계",
                scope: boundary.operationalScope || boundary.scopeRationale || "-",
                period: this.formatPeriod(boundary.startDate, boundary.endDate),
                remarks: boundary.remarks || "",
                draftStat: boundary.draftStat || "",
            }));
        },
        versionCards() {
            return (this.versions || [])
                .filter((version) => version.draftStat === "ACTIVE")
                .map((version) => ({
                    value: version.versionIdx,
                    label: version.versionName || version.versionCode || "배출계수 버전",
                    code: version.versionCode || version.versionIdx,
                    period: this.formatPeriod(version.validFrom, version.validTo),
                    isActive: version.isActive === 1 || version.isActive === true,
                    gwpVersion: version.gwpVersion || "",
                    draftStat: version.draftStat || "",
                }));
        },
        selectedVersion() {
            if (!this.local.versionIdx) return null;
            return (this.versions || []).find((version) => version.versionIdx === this.local.versionIdx) || null;
        },
        selectedOrgName() {
            if (!this.local.orgCode) return "";
            const org = this.organizationOptions.find((o) => o.value === this.local.orgCode);
            return org?.label || "";
        },
        selectedOrgBoundaryName() {
            if (!this.local.orgBoundaryIdx) return "";
            const boundary = this.orgBoundaryOptions.find((b) => b.value === this.local.orgBoundaryIdx);
            return boundary?.label || "";
        },
        selectedOperBoundaryName() {
            if (!this.local.operBoundaryIdx) return "";
            const boundary = this.operBoundaryOptions.find((b) => b.value === this.local.operBoundaryIdx);
            return boundary?.label || "";
        },
        isValid() {
            const { orgCode, period, orgBoundaryIdx, operBoundaryIdx, versionIdx } = this.local;
            return !!(orgCode && period && versionIdx && orgBoundaryIdx && operBoundaryIdx);
        },
        validationMessage() {
            if (!this.local.orgCode) {
                return "조직을 선택해주세요.";
            }
            if (!this.local.period) {
                return "기준 월을 선택해주세요.";
            }
            if (!this.local.orgBoundaryIdx) {
                return "조직 경계를 선택해주세요.";
            }
            if (!this.local.operBoundaryIdx) {
                return "운영 경계를 선택해주세요.";
            }
            if (!this.local.versionIdx) {
                return "배출계수 버전을 선택해주세요.";
            }
            return "";
        },
    },
    watch: {
        value: {
            handler(newValue) {
                if (this.isParamsEqual(newValue, this.local)) return;
                this.local = {
                    orgCode: newValue.orgCode ?? this.selectedOrgCode ?? null,
                    period: newValue.period,
                    orgBoundaryIdx: newValue.orgBoundaryIdx ?? null,
                    operBoundaryIdx: newValue.operBoundaryIdx ?? null,
                    versionIdx: newValue.versionIdx ?? null,
                };
                this.submitted = false;
            },
            deep: true,
        },
        selectedOrgCode(newCode) {
            if (newCode === this.local.orgCode) return;
            // 상위에서 selectedOrgCode가 변경되면 local도 업데이트
            if (newCode !== this.local.orgCode) {
                this.local = {
                    ...this.local,
                    orgCode: newCode ?? null,
                    orgBoundaryIdx: null,
                    operBoundaryIdx: null,
                };
            }
        },
        "local.orgCode": {
            handler(newCode, oldCode) {
                // orgCode가 변경되면 API 호출을 위해 상위로 이벤트 전달
                // 초기화 시나 selectedOrgCode watch에서 변경된 경우는 제외
                if (newCode !== oldCode && oldCode !== undefined) {
                    // selectedOrgCode와 다를 때만 API 호출 (사용자가 직접 선택한 경우)
                    if (newCode !== this.selectedOrgCode) {
                        this.$nextTick(() => {
                            this.handleOrgSelect(newCode);
                        });
                    }
                }
            },
            immediate: false,
        },
        local: {
            handler(newValue) {
                if (this.isParamsEqual(newValue, this.value)) return;
                this.$emit("input", newValue);
                this.$emit("update:value", newValue);
            },
            deep: true,
        },
    },
    methods: {
        formatStatus(status) {
            return CarbonCodes.status.getLabel(status);
        },
        formatDraftStat(status) {
            return CarbonCodes.draftStat.getLabel(status);
        },
        isParamsEqual(a = {}, b = {}) {
            const keys = ["orgCode", "period", "orgBoundaryIdx", "operBoundaryIdx", "versionIdx"];
            const normalize = (source = {}) =>
                keys.reduce((acc, key) => {
                    acc[key] = source[key] ?? null;
                    return acc;
                }, {});
            const normalizedA = normalize(a);
            const normalizedB = normalize(b);
            return keys.every((key) => normalizedA[key] === normalizedB[key]);
        },
        handleOrgSelect(value) {
            this.$emit("select-org", value);
        },
        updateLocal(payload = {}) {
            this.local = {
                ...this.local,
                ...payload,
            };
        },
        selectOrganization(value) {
            if (this.loading) return;
            // local.orgCode를 변경하면 watch가 트리거되어 API 호출됨
            this.updateLocal({
                orgCode: value,
                orgBoundaryIdx: null,
                operBoundaryIdx: null,
            });
            // watch에서 처리하므로 여기서는 직접 호출하지 않음
        },
        selectOrgBoundary(value) {
            if (this.loading) return;
            this.updateLocal({ orgBoundaryIdx: value });
        },
        selectOperBoundary(value) {
            if (this.loading) return;
            this.updateLocal({ operBoundaryIdx: value });
        },
        selectVersion(value) {
            if (this.loading) return;
            this.updateLocal({ versionIdx: value });
        },
        clearSelection(field) {
            if (this.loading) return;

            if (field === "orgCode") {
                this.updateLocal({
                    orgCode: null,
                    orgBoundaryIdx: null,
                    operBoundaryIdx: null,
                });
                this.handleOrgSelect(null);
            } else if (field === "orgBoundaryIdx") {
                this.updateLocal({
                    orgBoundaryIdx: null,
                    operBoundaryIdx: null,
                });
            } else if (field === "operBoundaryIdx") {
                this.updateLocal({ operBoundaryIdx: null });
            } else if (field === "versionIdx") {
                this.updateLocal({ versionIdx: null });
            }
        },
        handleSubmit() {
            this.submitted = true;
            if (!this.isValid) return;
            this.$emit("submit", { ...this.local });
        },
        formatDate(value) {
            if (!value) return "-";
            try {
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) {
                    return value;
                }
                return date.toLocaleDateString("ko-KR");
            } catch (e) {
                return value;
            }
        },
        formatAccountingMethod(method) {
            return CarbonCodes.accountingMethod.getLabel(method);
        },
        formatPeriod(start, end) {
            if (!start && !end) return "";
            const fmt = (value) => (value ? value.replace(/-/g, ".") : "현재");
            return `${fmt(start)} ~ ${fmt(end)}`;
        },
    },
};
</script>

<style scoped>
.calc-run-form {
    background: #ffffff;
    padding: 0;
}

.form-body {
    padding: 20px 24px;
}

.form-section {
    padding: 18px 20px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    margin-bottom: 16px;
    background: #fafafa;
}

.form-section:last-of-type {
    margin-bottom: 20px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8e8e8;
}

.section-header h5 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #2c2c2c;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.section-header span {
    font-size: 11px;
    color: #999999;
    font-weight: 400;
}

.compact-alert {
    padding: 12px 16px;
    margin-bottom: 16px;
    border-radius: 6px;
}

.compact-grid {
    margin-top: 8px;
}

.action-row {
    display: flex;
    justify-content: flex-end;
    padding-top: 16px;
    border-top: 1px solid #e8e8e8;
}

.action-row :deep(.action-btn.btn-success) {
    background: #52b788;
    border: 1px solid #52b788;
    color: #ffffff;
    font-weight: 600;
    padding: 12px 32px;
    font-size: 14px;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.action-row :deep(.action-btn.btn-success:hover),
.action-row :deep(.action-btn.btn-success:focus) {
    background: #40916c;
    border-color: #40916c;
    box-shadow: 0 4px 12px rgba(82, 183, 136, 0.3);
}

.action-row :deep(.action-btn.btn-success:disabled) {
    background: #d0d0d0;
    border-color: #d0d0d0;
    color: #999999;
    opacity: 1;
}

/* Period flow */
.period-flow {
    display: flex;
    flex-direction: column;
    gap: 0;
    position: relative;
    padding-left: 0;
}

.period-step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    position: relative;
    min-height: 50px;
}

.period-step:not(:last-child) {
    padding-bottom: 16px;
}

.period-step:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 13px;
    top: 20px;
    bottom: 0;
    width: 2px;
    background: #52b788;
    z-index: 1;
    pointer-events: none;
}

.period-step .step-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    position: relative;
    z-index: 2;
    width: 28px;
}

.period-step .step-number {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #52b788;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    transition: all 0.2s ease;
    position: relative;
    z-index: 3;
    border: 2px solid #52b788;
    box-sizing: border-box;
}

.period-step .step-connector {
    display: none;
}

.period-step .step-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.period-step .step-label {
    font-size: 11px;
    font-weight: 600;
    color: #666666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
}

.period-picker {
    width: 100%;
}

/* 선택 가능한 카드 그리드 */
.step-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
    margin-top: 8px;
    /* 레이아웃 재계산 방지 */
    grid-auto-flow: row;
    align-items: start;
    /* 최소 높이 설정으로 레이아웃 시프트 방지 */
    min-height: 0;
}

.selectable-card {
    padding: 12px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 70px;
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 8px;
    /* hover 시 레이아웃 변경 방지 */
    box-sizing: border-box;
    position: relative;
}

.selectable-card:hover:not(.disabled) {
    border-color: #52b788;
    background: #f9f9f9;
}

.selectable-card:hover:not(.disabled)::before {
    border-color: #52b788;
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.1);
}

.selectable-card > * {
    position: relative;
    z-index: 1;
}

.selectable-card.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.selectable-card.card-active {
    border-color: #52b788;
    background: #f0fdf4;
}

.calc-card-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.card-badges {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.calc-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #2c2c2c;
    line-height: 1.4;
    word-break: break-word;
    flex: 1;
}

.card-meta-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.card-meta {
    font-size: 11px;
    color: #666666;
    line-height: 1.4;
    display: flex;
    align-items: center;
    gap: 6px;
}

.card-meta i {
    font-size: 10px;
    color: #999999;
    width: 12px;
    flex-shrink: 0;
}

.card-meta.card-scope {
    color: #666666;
    font-style: italic;
}

.card-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
}

.card-badge.badge-active {
    background: #dcfce7;
    color: #166534;
}

.card-badge.status-active {
    background: #dcfce7;
    color: #166534;
}

.card-badge.status-inactive {
    background: #fee2e2;
    color: #b91c1c;
}

.card-badge.status-draft {
    background: #fef9c3;
    color: #b45309;
}

.empty-message {
    grid-column: 1 / -1;
    padding: 16px;
    text-align: center;
    color: #999999;
    font-size: 12px;
    background: #f9f9f9;
    border: 1px dashed #e8e8e8;
    border-radius: 6px;
}

/* Transition 애니메이션 - 레이아웃 시프트 최소화 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Drill down flow */
.drill-down-flow {
    display: flex;
    flex-direction: column;
    gap: 0;
    position: relative;
    padding-left: 0;
}

.drill-step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    position: relative;
    transition: all 0.2s ease;
    min-height: 60px;
}

.drill-step:not(:last-child) {
    padding-bottom: 20px;
}

.drill-step.active {
    background: #f9f9f9;
    border-radius: 6px;
    z-index: 1;
}

.drill-step.completed .step-number {
    background: #52b788;
    color: #ffffff;
}

.step-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    position: relative;
    z-index: 2;
    width: 28px;
}

.step-number {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #ffffff;
    color: #999999;
    font-size: 12px;
    font-weight: 700;
    transition: all 0.2s ease;
    position: relative;
    z-index: 3;
    border: 2px solid #e8e8e8;
    box-sizing: border-box;
}

.drill-step.active .step-number {
    background: #52b788;
    color: #ffffff;
    border-color: #52b788;
}

.drill-step.completed .step-number {
    background: #52b788;
    color: #ffffff;
    border-color: #52b788;
}

/* 연결선: 각 단계 사이를 연결하는 세로선 - 패딩/마진에 영향받지 않도록 절대 위치 */
.drill-step:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 13px;
    top: 20px;
    bottom: 0;
    width: 2px;
    background: #52b788;
    z-index: 1;
    pointer-events: none;
}

/* 활성 단계나 완료된 단계 다음에 연결선 표시 */
.drill-step.active:not(:last-child)::before,
.drill-step.completed:not(:last-child)::before {
    display: block;
}

.step-connector {
    display: none; /* 개별 connector는 제거하고 ::before로 통일 */
}

.step-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.step-label {
    font-size: 11px;
    font-weight: 600;
    color: #666666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
}

.step-select {
    width: 100%;
}

.step-selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
}

.selected-value {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: #2c2c2c;
    min-width: 0;
    word-break: break-word;
}

.step-change-btn {
    flex-shrink: 0;
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    color: #666666;
    background: transparent;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.step-change-btn:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #a0a0a0;
    color: #2c2c2c;
}

.step-change-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.step-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #999999;
    margin-top: 4px;
}

.step-meta i {
    font-size: 10px;
}

/* Date picker custom styling */
:deep(.mx-datepicker) {
    width: 100%;
}

:deep(.mx-input) {
    height: 40px;
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    padding: 0 14px;
    font-size: 13px;
    color: #2c2c2c;
    transition: border-color 0.2s ease;
}

:deep(.mx-input:hover) {
    border-color: #a0a0a0;
}

:deep(.mx-input:focus) {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.2);
}

@media (max-width: 768px) {
    .form-body {
        padding: 16px 20px;
    }

    .form-section {
        padding: 14px 16px;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
}
</style>
